// The free onsite measure: one slot engine for both doors (S2 of the HiPages lead → onsite plan).
//
// PORTED from the proven artemis-website `api/src/schedule.ts` (the public /book flow that has been
// taking real bookings since September): weekdays only, the first visit starts 09:30 Sydney, each
// visit is 60 minutes, at least two hours' notice, at most 28 days ahead.
// CHANGED 12 Oct 2026 (Jacky): visits now end by 19:00 (was 16:00); candidate starts are a 30-minute grid
// (was a fixed 90-minute cadence); and travel is LOCATION-AWARE — see measureSlots — so a slot needs the
// real drive from the previous commitment and back to the next one. A slot is offered only then — and,
// since 12 Oct 2026, while a measurer (Jacky) is free on his own calendar ("availability slot = my
// free slot"). The backbone's getSchedule folds both into `busy`; this engine is unchanged.
//
// The numbers are configuration (`bb.config measure_booking.rules`), these are the defaults.
// All wall-clock arithmetic is Sydney's (AGENTS.md "One clock"), DST-correct through
// `sydneyWallTimeToIso`.
//
// NEAR (suburb clustering, the plan's "prefer slots adjacent to a booked measure nearby"): a slot is
// `near` when another lead's measure is booked on the SAME Sydney day and that lead is in the same
// suburb, the same postcode, or a postcode sharing the first three digits. NSW postcodes that share
// three leading digits are, almost always, neighbouring suburbs within roughly 5 km of each other —
// a deliberate stand-in for geocoding, simple enough to explain in one sentence and wrong in the
// safe direction (a missed `near` costs a little travel, never a booking). `near` is a flag; slots
// stay chronological.
import { z } from "zod";
import { shiftSydneyDay, sydneyDay, sydneyWallTimeToIso } from "./crew-calendar.js";
export const MeasureSlotRules = z.object({
    first_start: z.string().regex(/^\d{2}:\d{2}$/).default("09:30"),
    // Jacky, 12 Oct 2026: "booking time for onsite assessment is opened up till 7pm" — the last visit ends by 19:00.
    last_end: z.string().regex(/^\d{2}:\d{2}$/).default("19:00"),
    visit_minutes: z.number().int().min(15).max(240).default(60),
    /** Candidate starts are this far apart (12 Oct 2026: a 30-minute grid; travel decides which survive). */
    slot_step_minutes: z.number().int().min(15).max(120).default(30),
    /** Superseded 12 Oct 2026 by location-aware travel (below). Kept so stored configs still parse; unused. */
    travel_minutes: z.number().int().min(0).max(180).default(30),
    /** Drive time between two known places: base + per straight-line km, rounded up to 5, clamped. */
    travel_base_minutes: z.number().int().min(0).max(60).default(10),
    travel_minutes_per_km: z.number().min(0.5).max(10).default(2.3),
    travel_min_minutes: z.number().int().min(0).max(120).default(15),
    travel_max_minutes: z.number().int().min(15).max(240).default(120),
    /** Drive time when either end has no location (e.g. a measurer's own calendar shared as free/busy only). */
    unknown_travel_minutes: z.number().int().min(0).max(180).default(45),
    min_notice_minutes: z.number().int().min(0).max(7 * 24 * 60).default(120),
    max_days: z.number().int().min(1).max(60).default(28),
});
export const MEASURE_SLOT_DEFAULTS = MeasureSlotRules.parse({});
export const MEASURE_TIMEZONE = "Australia/Sydney";
const minutes = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };
const hhmm = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
/** "Tue 14 Oct, 10:30am" — the Sydney wall time a person reads. */
export function measureSlotLabel(iso) {
    const at = new Date(iso);
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-AU", { timeZone: MEASURE_TIMEZONE, weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true })
        .formatToParts(at).map((x) => [x.type, x.value]));
    return `${p.weekday} ${p.day} ${p.month}, ${p.hour}:${p.minute}${String(p.dayPeriod ?? "").toLowerCase().replace(/\./g, "")}`;
}
export function sydneyWeekday(day) {
    const noon = sydneyWallTimeToIso(`${day}T12:00`);
    return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(new Intl.DateTimeFormat("en-US", { timeZone: MEASURE_TIMEZONE, weekday: "short" }).format(new Date(noon)));
}
/** Every candidate start in one Sydney business day, ignoring busy state. Weekends have none. */
export function candidateMeasureSlots(day, rules = MEASURE_SLOT_DEFAULTS) {
    const wd = sydneyWeekday(day);
    if (wd === 0 || wd === 6)
        return [];
    const out = [];
    const step = rules.slot_step_minutes;
    for (let s = minutes(rules.first_start); s + rules.visit_minutes <= minutes(rules.last_end); s += step) {
        out.push({ start: sydneyWallTimeToIso(`${day}T${hhmm(s)}`), end: sydneyWallTimeToIso(`${day}T${hhmm(s + rules.visit_minutes)}`) });
    }
    return out;
}
const overlaps = (a, b) => Date.parse(a.start) < Date.parse(b.end) && Date.parse(b.start) < Date.parse(a.end);
/** Same suburb, same postcode, or the same three leading postcode digits. */
export function nearBy(a, b) {
    const s1 = a.suburb?.trim().toLowerCase(), s2 = b.suburb?.trim().toLowerCase();
    if (s1 && s2 && s1 === s2)
        return true;
    const p1 = a.postcode?.trim(), p2 = b.postcode?.trim();
    return !!p1 && !!p2 && /^\d{4}$/.test(p1) && /^\d{4}$/.test(p2) && p1.slice(0, 3) === p2.slice(0, 3);
}
/** The window a slot read must ask the calendar about: today .. today+days-1, capped by the rules. */
export function measureWindow(now, days, rules = MEASURE_SLOT_DEFAULTS) {
    const today = sydneyDay(now);
    const n = Math.max(1, Math.min(days, rules.max_days));
    const list = Array.from({ length: n }, (_, i) => shiftSydneyDay(today, i));
    return { days: list, from: sydneyWallTimeToIso(`${list[0]}T00:00`), to: sydneyWallTimeToIso(`${shiftSydneyDay(list[list.length - 1], 1)}T00:00`) };
}
/** Straight-line distance in km. */
export function kmBetween(a, b) {
    const r = (d) => (d * Math.PI) / 180, R = 6371;
    const dLat = r(b.lat - a.lat), dLng = r(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}
/** Minutes to drive between two places. A deliberate, explainable estimate (12 Oct 2026): 10 minutes to get
 *  going and park, plus 2.3 minutes per straight-line km (Sydney roads run about 1.35x the straight line at
 *  about 35 km/h), rounded up to 5, at least 15 and at most 120. Either end unknown: 45 minutes. */
export function travelMinutes(a, b, rules = MEASURE_SLOT_DEFAULTS) {
    if (!a || !b)
        return rules.unknown_travel_minutes;
    const raw = rules.travel_base_minutes + rules.travel_minutes_per_km * kmBetween(a, b);
    return Math.min(rules.travel_max_minutes, Math.max(rules.travel_min_minutes, Math.ceil(raw / 5) * 5));
}
/** Free slots, chronological.
 *
 *  LOCATION-AWARE TRAVEL (12 Oct 2026). Jacky: "leave me 1 hour between jobs + travel time (and ensure I can get
 *  back to scheduled job on time)". A slot is offered only when, on its day:
 *    - its 60-minute visit overlaps no commitment (sales@ busy, a measurer busy on their own calendar, a held
 *      measure);
 *    - the drive from the commitment that ends last before it, from THAT commitment's location to the
 *      customer's, fits between that commitment's end and the visit's start;
 *    - the drive from the customer to the next commitment's location fits between the visit's end and that
 *      commitment's start — he arrives on time.
 *  The first visit of a clear day starts at `first_start`; the last ends by `last_end`. */
export function measureSlots(input) {
    const rules = input.rules ?? MEASURE_SLOT_DEFAULTS;
    const earliest = input.now.getTime() + rules.min_notice_minutes * 60000;
    const commitments = [...input.busy, ...input.booked.filter((b) => b.lead_id !== input.lead.id)]
        .map((c) => ({ s: Date.parse(c.start), e: Date.parse(c.end), where: c.where ?? null }))
        .filter((c) => Number.isFinite(c.s) && Number.isFinite(c.e));
    const here = input.lead.where ?? null;
    const out = [];
    for (const day of measureWindow(input.now, input.days, rules).days) {
        const neighbours = input.booked.filter((b) => b.lead_id !== input.lead.id && sydneyDay(new Date(b.start)) === day && nearBy(b, input.lead));
        for (const slot of candidateMeasureSlots(day, rules)) {
            const s = Date.parse(slot.start), e = Date.parse(slot.end);
            if (s < earliest)
                continue;
            if (commitments.some((c) => c.s < e && s < c.e))
                continue;
            const before = commitments.filter((c) => c.e <= s && sydneyDay(new Date(c.e)) === day).sort((a, b) => b.e - a.e)[0];
            if (before && s - before.e < travelMinutes(before.where, here, rules) * 60000)
                continue;
            const after = commitments.filter((c) => c.s >= e && sydneyDay(new Date(c.s)) === day).sort((a, b) => a.s - b.s)[0];
            if (after && after.s - e < travelMinutes(here, after.where, rules) * 60000)
                continue;
            out.push({ ...slot, label: measureSlotLabel(slot.start), near: neighbours.length > 0 });
        }
    }
    return out;
}
/** Is this exact start/end an offered slot shape (a cadence start on a weekday, inside the window)? */
export function isMeasureSlotShape(start, end, now, rules = MEASURE_SLOT_DEFAULTS) {
    const s = Date.parse(start), e = Date.parse(end);
    if (!Number.isFinite(s) || !Number.isFinite(e))
        return false;
    const day = sydneyDay(new Date(s));
    const { days } = measureWindow(now, rules.max_days, rules);
    if (!days.includes(day))
        return false;
    return candidateMeasureSlots(day, rules).some((c) => Date.parse(c.start) === s && Date.parse(c.end) === e);
}
export const MeasureSlotsResponse = z.object({
    slots: z.array(z.object({ start: z.iso.datetime({ offset: true }), end: z.iso.datetime({ offset: true }), label: z.string(), near: z.boolean() })),
    timezone: z.literal(MEASURE_TIMEZONE),
});
//# sourceMappingURL=measure-slots.js.map